import Cookies from "universal-cookie";
import {
  getCurrentHostname,
  getSharedAuthCookieDomain,
  isLocalHostname,
  resolveTenantHostname,
} from "./tenantHost";

const HANDOFF_PREFIX = "re_handoff=";

function toBase64Url(value) {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(value) {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (padded.length % 4)) % 4;
  return decodeURIComponent(
    escape(atob(`${padded}${"=".repeat(padLength)}`))
  );
}

function persistTokens({ accessToken, refreshToken, rememberMe }) {
  const tokenStorage = rememberMe ? localStorage : sessionStorage;
  const otherStorage = rememberMe ? sessionStorage : localStorage;
  const cookieDomain = getSharedAuthCookieDomain();

  if (accessToken) {
    tokenStorage.setItem("authToken", accessToken);
    otherStorage.removeItem("authToken");
    const cookies = new Cookies();
    cookies.set("token", accessToken, {
      path: "/",
      ...(cookieDomain ? { domain: cookieDomain } : {}),
      secure: window.location.protocol === "https:",
      sameSite: "lax",
      expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    });
  }

  if (refreshToken) {
    tokenStorage.setItem("refreshToken", refreshToken);
    otherStorage.removeItem("refreshToken");
  } else {
    tokenStorage.removeItem("refreshToken");
    otherStorage.removeItem("refreshToken");
  }
}

function readStoredTokens() {
  const accessToken =
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("authToken") ||
    "";
  const refreshToken =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken") ||
    "";
  const rememberMe = Boolean(localStorage.getItem("authToken"));
  return { accessToken, refreshToken, rememberMe };
}

export function consumeTenantAuthHandoff() {
  if (typeof window === "undefined") return false;
  const hash = String(window.location.hash || "").replace(/^#/, "");
  if (!hash.startsWith(HANDOFF_PREFIX)) return false;

  try {
    const encoded = hash.slice(HANDOFF_PREFIX.length);
    const payload = JSON.parse(fromBase64Url(encoded));
    const accessToken = payload.accessToken || payload.t || "";
    const refreshToken = payload.refreshToken || payload.r || "";
    const rememberMe = Boolean(payload.rememberMe || payload.m);
    const user = payload.user || payload.u || null;
    const organization = payload.organization || payload.o || null;
    const features = Array.isArray(payload.features)
      ? payload.features
      : Array.isArray(payload.f)
        ? payload.f
        : [];

    persistTokens({ accessToken, refreshToken, rememberMe });

    if (user) {
      localStorage.setItem(
        "userState",
        JSON.stringify({
          user,
          isAuthenticated: true,
          features,
          organization,
        })
      );
    }

    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", cleanUrl);
    return true;
  } catch (error) {
    console.warn("Failed to consume tenant auth handoff:", error);
    return false;
  }
}

function getTenantLandingPath(organization) {
  const orgTypes = Array.isArray(organization?.organizationType)
    ? organization.organizationType
    : organization?.organizationType
      ? [organization.organizationType]
      : [];
  if (orgTypes.includes("schoolTrack") && !orgTypes.includes("vehicleTrack")) {
    return "/school";
  }
  return "/dashboard";
}

function compactFeatures(features) {
  if (!Array.isArray(features)) return [];
  return features.map((feature) => ({
    action: feature?.action || "",
    api_path: feature?.api_path || "",
    name: feature?.name || "",
    displayName: feature?.displayName || "",
    isDefault: Boolean(feature?.isDefault),
  }));
}

function buildHandoffUrl(tenantHost, landingPath, payload) {
  return `https://${tenantHost}${landingPath}#${HANDOFF_PREFIX}${toBase64Url(
    JSON.stringify(payload)
  )}`;
}

function leaveForTenant(url) {
  window.location.replace(url);
}

export function redirectToTenantHost({
  organization,
  user,
  features = [],
  accessToken: explicitAccessToken,
  refreshToken: explicitRefreshToken,
  rememberMe: explicitRememberMe,
} = {}) {
  const tenantHost = resolveTenantHostname(organization);
  const currentHost = getCurrentHostname();
  const stored = readStoredTokens();
  const accessToken = explicitAccessToken || stored.accessToken;
  const refreshToken =
    explicitRefreshToken !== undefined && explicitRefreshToken !== null
      ? explicitRefreshToken
      : stored.refreshToken;
  const rememberMe =
    explicitRememberMe !== undefined ? explicitRememberMe : stored.rememberMe;

  if (!tenantHost || isLocalHostname(currentHost) || tenantHost === currentHost) {
    console.warn("[tenant-redirect] skipped", {
      currentHost,
      tenantHost,
      local: isLocalHostname(currentHost),
      hasToken: Boolean(accessToken),
      hasAppHostname: Boolean(organization?.appHostname),
      hasSlug: Boolean(organization?.subdomainSlug),
    });
    return false;
  }
  if (!accessToken) {
    console.warn("[tenant-redirect] skipped: missing access token", {
      currentHost,
      tenantHost,
    });
    return false;
  }

  persistTokens({ accessToken, refreshToken, rememberMe });

  const landingPath = getTenantLandingPath(organization);
  const payload = {
    t: accessToken,
    r: refreshToken,
    m: rememberMe,
    u: user,
    o: organization,
    f: compactFeatures(features),
  };
  const url = buildHandoffUrl(tenantHost, landingPath, payload);

  leaveForTenant(url);
  return true;
}

export const __testables = {
  compactFeatures,
  toBase64Url,
  fromBase64Url,
  buildHandoffUrl,
  getTenantLandingPath,
};
